import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { api, qs } from '@/lib/api';
import { CALENDAR_KINDS, KIND_ORDER, MONTHS_AR, addMonths, daysInMonth, groupByDay, longDateFmt, monthGridWindow, monthKeyOf, pad2, todayKey, type CalendarKind, type CalendarResponse } from '@/lib/calendar';
import { MonthGrid } from './MonthGrid';

/**
 * منتقي التاريخ بالتقويم (11 أكتوبر 2026) — يحلّ محلّ قوائم يوم/شهر/سنة في كل حقل `type="date"` و`"datetime-local"`
 * (طلبُ صاحب المنصة: «اختيارُ الأيام بقوائم سيءٌ لتجربة العميل … أبيه يظهر جدول التقويم مربّعات»).
 *
 * الزرُّ يعرض التاريخَ بكلماته، والضغطُ يفتح شبكةَ الشهر؛ ولمن دخل المنصةَ تظهر تحت كل يومٍ نقاطُ ما هو محجوزٌ له فيه
 * (من `GET /me/calendar`) — فمن يجدول جلسةً يرى يومَه المزدحم قبل أن يختاره. وتاريخُ الميلاد (`past`) بلا نقاط وبسنواتٍ إلى الوراء.
 * العقدُ كعقد `DateParts` بالضبط: `value` بصيغة ISO، و`onChange({ target: { value } })`، والخارجُ عن `min/max` يُسلَّم فارغاً مع رسالة.
 * واللوحُ يُرسم في `body` (portal) بموضعٍ ثابت فلا يقصّه غلافُ نافذةٍ، وعلى الهاتف ورقةٌ من الأسفل.
 */
interface Props {
  kind: 'date' | 'datetime-local';
  id?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  past?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const dateOf = (v: string) => v.slice(0, 10);
const timeOf = (v: string) => (v.length >= 16 ? v.slice(11, 16) : '');
const QUICK_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16];

export function DatePickerField({ kind, id, value, onChange, min, max, required, disabled, name, past, ...aria }: Props) {
  void required; // الإلزامُ يفحصه النموذجُ والخادم؛ الزرُّ لا يحمل تحقّقَ المتصفّح
  const { me } = useAuth();
  const v = value ?? '';
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState<string>(dateOf(v));
  const [time, setTime] = useState<string>(timeOf(v));
  const [month, setMonth] = useState<string>(() => monthKeyOf(dateOf(v) || todayKey()));
  const [rangeErr, setRangeErr] = useState<string | null>(null);
  // قيمةٌ تتغيّر من الخارج تُعيد الحالة؛ وما أرسله الحقلُ نفسُه لا يعيدها (كـDateParts) — في الرسم لا في أثر
  const [seen, setSeen] = useState(v);
  const [emitted, setEmitted] = useState(v);
  if (v !== seen) {
    setSeen(v);
    if (v !== emitted) { setDay(dateOf(v)); setTime(timeOf(v)); if (dateOf(v)) setMonth(monthKeyOf(dateOf(v))); setRangeErr(null); setEmitted(v); }
  }

  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const minDay = min ? dateOf(min) : undefined;
  const maxDay = max ? dateOf(max) : undefined;
  const nowY = Number(todayKey().slice(0, 4));
  const years = useMemo(() => {
    const lo = minDay ? Number(minDay.slice(0, 4)) : past ? nowY - 90 : nowY - 10;
    const hi = maxDay ? Number(maxDay.slice(0, 4)) : past ? nowY : nowY + 5;
    const list: number[] = [];
    for (let y = Math.min(lo, hi); y <= Math.max(lo, hi); y++) list.push(y);
    const cur = Number(month.slice(0, 4));
    if (!list.includes(cur)) list.push(cur);
    return list.sort((a, b) => (past ? b - a : a - b));
  }, [minDay, maxDay, past, nowY, month]);

  // مواعيدي في الشهر المعروض — لمن دخل، وليس لتاريخ ميلاد
  const win = monthGridWindow(month);
  const busy = useQuery({
    queryKey: ['calendar', 'picker', win.from, win.to],
    queryFn: () => api.get<CalendarResponse>(`/me/calendar${qs({ from: win.from, to: win.to })}`),
    enabled: open && !!me && !past,
    staleTime: 60_000,
  });
  const byDay = useMemo(() => groupByDay(busy.data?.items ?? []), [busy.data]);
  const legendKinds = useMemo(() => KIND_ORDER.filter((k) => (busy.data?.items ?? []).some((i) => i.kind === k)), [busy.data]);

  const emit = (d: string, t: string) => {
    let out = kind === 'date' ? d : d && t ? `${d}T${t}` : '';
    if (out && ((min && out < min.slice(0, out.length)) || (max && out > max.slice(0, out.length)))) {
      setRangeErr(min && out < min.slice(0, out.length) ? 'التاريخ قبل أقرب تاريخٍ مسموح' : 'التاريخ بعد آخر تاريخٍ مسموح');
      out = '';
    } else setRangeErr(null);
    setEmitted(out);
    onChange?.({ target: { value: out, name }, currentTarget: { value: out, name } } as unknown as ChangeEvent<HTMLInputElement>);
  };

  const close = () => { setOpen(false); btnRef.current?.focus(); };
  const pickDay = (d: string) => {
    setDay(d); setMonth(monthKeyOf(d));
    if (kind === 'date') { emit(d, ''); setOpen(false); btnRef.current?.focus(); }
    else emit(d, time);
  };
  const pickTime = (t: string) => { setTime(t); emit(day, t); };

  // الموضع: تحت الزرّ إن اتّسع، وإلا فوقه؛ ويبقى داخل الشاشة أفقياً (إحداثياتٌ فيزيائية — هندسةٌ لا اتجاه)
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = btnRef.current?.getBoundingClientRect(); const p = popRef.current;
      if (!b || !p) return;
      const w = p.offsetWidth; const h = p.offsetHeight;
      const below = b.bottom + 6; const above = b.top - 6 - h;
      const top = below + h <= window.innerHeight || above < 8 ? Math.min(below, Math.max(8, window.innerHeight - h - 8)) : above;
      const left = Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, month, kind]);

  // أولُ تركيزٍ داخل اللوح على اليوم المختار أو اليوم
  useEffect(() => {
    if (!open) return;
    const target = popRef.current?.querySelector<HTMLElement>(`[data-day="${day || todayKey()}"]`) ?? popRef.current?.querySelector<HTMLElement>('.mgrid__day:not(:disabled)');
    target?.focus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); e.nativeEvent.stopPropagation(); close(); return; }
    // الأسهمُ بين الأيام
    const active = document.activeElement as HTMLElement | null;
    const d = active?.dataset.day;
    if (!d || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const delta = e.key === 'ArrowUp' ? -7 : e.key === 'ArrowDown' ? 7 : e.key === 'ArrowRight' ? -1 : 1; // RTL: اليمينُ يرجع يوماً
    const [y, m, dd] = d.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, dd + delta)).toISOString().slice(0, 10);
    if (monthKeyOf(next) !== month) setMonth(monthKeyOf(next));
    requestAnimationFrame(() => popRef.current?.querySelector<HTMLElement>(`[data-day="${next}"]`)?.focus());
  };

  const shown = day ? longDateFmt.format(new Date(`${day}T12:00:00+03:00`)) : '';
  const invalid = aria['aria-invalid'] || !!rangeErr;
  const [yy, mm] = month.split('-').map(Number);
  const hours = Array.from({ length: 24 }, (_, i) => pad2(i));
  const minutes = useMemo(() => { const l = Array.from({ length: 12 }, (_, i) => pad2(i * 5)); const cur = time.slice(3, 5); if (cur && !l.includes(cur)) l.push(cur); return l.sort(); }, [time]);

  return (
    <div className="dpk">
      <button
        ref={btnRef} type="button" id={id} className={`input dpk__btn${invalid ? ' is-invalid' : ''}`} disabled={disabled}
        aria-haspopup="dialog" aria-expanded={open} aria-invalid={invalid || undefined} aria-describedby={aria['aria-describedby']}
        onClick={() => setOpen((o) => !o)}
      >
        {day
          ? <span className="dpk__value">{shown}{kind === 'datetime-local' && time ? ` · ${time}` : kind === 'datetime-local' ? ' · اختر الوقت' : ''}</span>
          : <span className="dpk__placeholder">{kind === 'date' ? 'اختر تاريخاً من التقويم' : 'اختر الموعد من التقويم'}</span>}
        <CalendarDays size={18} aria-hidden />
      </button>
      {name && <input type="hidden" name={name} value={emitted} />}
      {rangeErr && <span className="field__error" role="alert">{rangeErr}</span>}

      {open && createPortal(
        <>
          <div className="dpk__scrim" onMouseDown={close} aria-hidden />
          <div
            ref={popRef} className="dpk__pop" role="dialog" aria-modal="false" aria-label={kind === 'date' ? 'اختيار التاريخ' : 'اختيار الموعد'}
            style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' }} onKeyDown={onKey}
          >
            <div className="dpk__head">
              <button type="button" className="dpk__nav" onClick={() => setMonth(addMonths(month, -1))} aria-label="الشهر السابق"><ChevronRight size={16} aria-hidden /></button>
              <select className="select" aria-label="الشهر" value={mm} onChange={(e) => setMonth(`${yy}-${pad2(Number(e.target.value))}`)}>
                {MONTHS_AR.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
              </select>
              <select className="select dpk__year" aria-label="السنة" value={yy} onChange={(e) => setMonth(`${e.target.value}-${pad2(mm)}`)}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <button type="button" className="dpk__nav" onClick={() => setMonth(addMonths(month, 1))} aria-label="الشهر التالي"><ChevronLeft size={16} aria-hidden /></button>
            </div>
            <MonthGrid monthKey={month} byDay={byDay} selected={day || null} onPickDay={pickDay} min={minDay} max={maxDay} compact idPrefix={`dpk-${id ?? 'x'}`} />
            {kind === 'datetime-local' && (
              <div className="dpk__time">
                <span className="caption">الوقت</span>
                <select className="select" aria-label="الساعة" value={time.slice(0, 2)} onChange={(e) => pickTime(`${e.target.value}:${time.slice(3, 5) || '00'}`)}>
                  <option value="">الساعة</option>
                  {hours.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <select className="select" aria-label="الدقيقة" value={time.slice(3, 5)} onChange={(e) => pickTime(`${time.slice(0, 2) || '09'}:${e.target.value}`)}>
                  <option value="">الدقيقة</option>
                  {minutes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <div className="dpk__chips" aria-label="أوقات سريعة">
                  {QUICK_HOURS.map((h) => <button key={h} type="button" className={`dpk__chip${time === `${pad2(h)}:00` ? ' is-on' : ''}`} onClick={() => pickTime(`${pad2(h)}:00`)}>{pad2(h)}:00</button>)}
                </div>
              </div>
            )}
            <div className="dpk__foot">
              {legendKinds.length > 0
                ? <span className="dpk__legend">{legendKinds.map((k: CalendarKind) => <span key={k} className={CALENDAR_KINDS[k].className}><i aria-hidden />{CALENDAR_KINDS[k].labelAr}</span>)}</span>
                : <span className="caption">{me && !past ? 'النقاطُ تحت اليوم: ما هو محجوزٌ لك فيه.' : ''}</span>}
              <div className="row gap-2">
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => pickDay(todayKey())} disabled={Boolean((minDay && todayKey() < minDay) || (maxDay && todayKey() > maxDay))}>اليوم</button>
                {kind === 'datetime-local' && <button type="button" className="btn btn--primary btn--sm" onClick={close} disabled={!day || !time}>تم</button>}
                {kind === 'date' && <button type="button" className="btn btn--ghost btn--sm" onClick={close}>إغلاق</button>}
              </div>
            </div>
            <span className="sr-only">{`${daysInMonth(yy, mm)} يوماً في هذا الشهر`}</span>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
